import * as t from 'io-ts';
import {isString} from 'lodash';

import {makeEnumRuntimeType} from '../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {makeApiType} from '../../../models/api/apiTypes';
import {RegexCharacterClassesEnum} from '../commons/stringTemplateModel';
import {TransformerIdsEnum} from './expressionInferHelpers';
import {ResultTypesEnum} from './infer/internal';

/*
 * Client id.
 */

export const RClientId = t.readonly(
  t.type({
    clientId: t.string,
  }),
);

const RWithClientId = <C extends t.Mixed>(codec: C) => t.intersection([codec, RClientId]);

/*
 * Expression id.
 */

interface ExpressionIdBrand {
  readonly ExpressionId: unique symbol;
}

// ExpressionId could be:
//  - a string that starts with `local:`, OR
//  - an immediate value (e.g. `reference` or `url`), OR
//  - 'previous_result'.
// Because we don't want to make the app crash when decoding unknown values coming from the BE,
// we're just checking that the value is a string.
export const RExpressionId = t.brand(
  t.string,
  (str): str is t.Branded<string, ExpressionIdBrand> => isString(str),
  'ExpressionId',
);

/*
 * Expression ref.
 */

export const RExpressionRef = t.readonly(
  t.type({
    expression: RExpressionId,
  }),
);

const RWithExpressionRef = <C extends t.Mixed>(codec: C) => t.union([codec, RExpressionRef]);

// @TODO: Use RTeammate id instead of t.Integer.
export const RTeammateIdOfRef = RWithExpressionRef(t.Integer);

/*
 * Regex part.
 */

export const RRegexPart = t.readonly(
  t.intersection([
    t.type({
      char: makeEnumRuntimeType(RegexCharacterClassesEnum),
    }),
    t.partial({
      // @TODO: Remove t.null when removed from the BE.
      repeat: t.union([t.number, t.null]),
    }),
  ]),
);

/*
 * String template.
 */

export const RStringTemplatePart = t.union([RRegexPart, RExpressionRef, t.string]);

export const RStringTemplate = t.readonly(
  t.type({
    template: t.readonlyArray(RStringTemplatePart),
  }),
);

export const RStringOrTemplate = t.union([RStringTemplate, t.string]);

/*
 * Expression result.
 */

const RResultType = makeEnumRuntimeType(ResultTypesEnum);

interface InferredValueType {
  type: ResultTypesEnum;
}

interface InferredValueTypeOutput {
  type: string | number; // Output type of `EnumType` is `string | number`.
}

const RInferredValueType: t.Type<InferredValueType, InferredValueTypeOutput> = t.recursion(
  'InferredValueType',
  () =>
    t.type({
      type: RResultType,
    }),
);

interface InferredListType {
  type: ResultTypesEnum.LIST;
  itemType: InferredType;
}

interface InferredListTypeOutput {
  type: ResultTypesEnum.LIST;
  itemType: InferredListType | InferredValueTypeOutput;
}

const RInferredListType: t.Type<InferredListType, InferredListTypeOutput> = t.recursion(
  'InferredListType',
  () =>
    t.type({
      type: t.literal(ResultTypesEnum.LIST),
      itemType: RInferredType,
    }),
);

const RInferredType = t.union([RInferredValueType, RInferredListType]);
type InferredType = t.TypeOf<typeof RInferredType>;

/*
 * Dry run snapshot.
 */

const RDryRunSnapshot = t.readonly(
  t.type({
    type: RResultType,
    value: t.string,
  }),
);

/*
 * Atom.
 */

const RArrayValue = t.readonlyArray(t.union([RExpressionRef, t.number]));

const RAtomParams = t.record(
  t.string,
  t.union([RArrayValue, RExpressionRef, RStringOrTemplate, t.number, t.string, t.boolean, t.undefined]),
);

export type AtomParams = t.TypeOf<typeof RAtomParams>;

type AtomParamsOutput = t.OutputOf<typeof RAtomParams>;

export interface PreAtom {
  transform: TransformerIdsEnum;
  params?: AtomParams;
  functionAtoms?: ReadonlyArray<PreAtom>;
}

interface PreAtomOutput {
  transform: string | number; // Output type of `EnumType` is `string | number`.
  params?: AtomParamsOutput;
  functionAtoms?: ReadonlyArray<PreAtomOutput>;
}

const RPreAtom: t.Type<PreAtom, PreAtomOutput> = t.recursion('PreAtom', () =>
  t.intersection([
    t.type({
      transform: makeEnumRuntimeType(TransformerIdsEnum),
    }),
    t.partial({
      params: RAtomParams,
      functionAtoms: t.readonlyArray(RPreAtom),
    }),
  ]),
);

const RUninferredAtom = t.readonly(
  t.intersection([
    t.type({
      previousInfer: RInferredType,
      transform: makeEnumRuntimeType(TransformerIdsEnum),
      params: RAtomParams,
      isValid: t.boolean,
    }),
    t.partial({
      // @TODO: Fix this.
      // functionAtoms: t.readonlyArray(RClientAtom),
      errorMessage: t.string,
    }),
  ]),
);

const RAtom = t.readonly(
  t.intersection([
    RUninferredAtom,
    t.type({
      inferredType: RInferredType,
    }),
    t.partial({
      dryRun: RDryRunSnapshot,
    }),
  ]),
);

const RClientAtom = RWithClientId(RAtom);

/*
 * Expression.
 */

export const RPreExpression = t.readonly(
  t.type({
    id: RExpressionId,
    name: t.string,
    atoms: t.readonlyArray(RPreAtom),
  }),
);

export const RExpression = t.readonly(
  t.intersection([
    t.type({
      id: RExpressionId,
      name: t.string,
      defaultName: t.string,
      atoms: t.readonlyArray(RClientAtom),
      inferredType: RInferredType,
    }),
    t.partial({
      dryRun: RDryRunSnapshot,
    }),
  ]),
);

const RExpressionsDryRunResult = t.readonly(
  t.type({
    expressions: t.readonlyArray(RExpression),
  }),
);

interface ExpressionsDryRunResultModel extends t.TypeOf<typeof RExpressionsDryRunResult> {}

export interface Expression extends t.TypeOf<typeof RExpression> {}

/*
 * IO methods.
 */

function parseExpressionsDryRunResult(src: any): ExpressionsDryRunResultModel {
  return {
    expressions: src.expressions,
  };
}

/*
 * API types.
 */

export const ApiExpressionsDryRunResult = makeApiType(
  'expressionsDryRunResult',
  RExpressionsDryRunResult,
  parseExpressionsDryRunResult,
);
